import { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation, Trans } from 'react-i18next'
import { ReactComponent as RoundedPlusIcon } from '../../../../assets/images/common/icons/rounded-plus-icon.svg'
import { ReactComponent as ResetIcon } from '../../../../assets/images/common/icons/reset-icon.svg'
import { ReactComponent as PackageIcon } from '../../../../assets/images/common/icons/package-icon.svg'
import { ReactComponent as WarningIcon } from '../../../../assets/images/common/icons/warning-icon.svg'
import { deleteAllSessions, deleteSession } from '../../../../reducers/programBuilderReducer'
import ConfirmationModal2 from '../../../common/ConfirmationModal2'
import RecurringSessionsModal from './RecurringSessionsModal'
import SingleSessionModal from './SingleSessionModal'
import CopyScheduleModal from './CopyScheduleModal'
import FieldError from '../../../common/FieldError'
import SessionCard from './SessionCard'

import './Schedule.scss'

const Schedule = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const errors = useSelector(state => state.programBuilder.errors)
  const sessions = useSelector(state => state.programBuilder.schedule.scheduleSessions)
  const filteredSessions = sessions.filter(s => !s.deleted)

  const [selectedSession, setSelectedSession] = useState(null)
  const [showSingleSessionModal, setShowSingleSessionModal] = useState(false)
  const [showRecurringSessionsModal, setShowRecurringSessionsModal] = useState(false)
  const [showCopyScheduleModal, setShowCopyScheduleModal] = useState(false)
  const [showConfirmationModal, setShowConfirmationModal] = useState(false)

  const handleEditSession = (session) => {
    setSelectedSession(session)
    setShowSingleSessionModal(true)
  }

  const handleDeleteSession = (session) => {
    dispatch(deleteSession(session))
  }

  const handleDeleteAllSessions = () => {
    dispatch(deleteAllSessions())
    setShowConfirmationModal(false)
  }

  return (
    <section id='Schedule'>
      <p className='title'>{t('programs.builder.schedule.title')}</p>
      <p>
        <Trans
          i18nKey='programs.builder.schedule.intro'
          components={{
            link: (
              // eslint-disable-next-line jsx-a11y/anchor-has-content
              <a
                href='https://docs.google.com/document/d/1rAsNcVFMDh5u-SQq9f2vjD9gkJ5ZaFBxZBx6C-TdE6w'
                target='_blank'
              />
            ),
          }}
        />
      </p>

      {errors.scheduleSessions && <FieldError message={errors.scheduleSessions} />}

      {filteredSessions.length > 0 && (
        <>
          <div className='sessions mt-4'>
            {filteredSessions.map((session) => (
              <SessionCard key={session.index} session={session} handleEditSession={handleEditSession} handleDeleteSession={handleDeleteSession} />
            ))}
          </div>
          <div className='d-flex justify-content-between w-100 mt-3'>
            <button className='d-flex align-items-center btn btn-primary add-session-button' onClick={() => setShowSingleSessionModal(true)}>
              <RoundedPlusIcon id='sessionRoundedPlusIcon' className='mr-2' /> {t('programs.builder.schedule.addSession')}
            </button>
            <button className='btn btn-primary delete-all-sessions-button' onClick={() => setShowConfirmationModal(true)}>
              {t('programs.builder.schedule.deleteAll')}
            </button>
          </div>
        </>
      )}

      {filteredSessions.length === 0 && (
        <div className='row mt-4'>
          <div className='col-12 col-lg-4 mb-3 p-2'>
            <div className='session-builder-card' aria-hidden='true' onClick={() => setShowSingleSessionModal(true)}>
              <RoundedPlusIcon aria-hidden='true' id='roundedPlusIcon' />
              <p className='session-card-title'>{t('programs.builder.schedule.createNewSession')}</p>
              <p className='session-card-description'>
                {t('programs.builder.schedule.createNewSessionDescription')}
              </p>
            </div>
          </div>
          <div className='col-12 col-lg-4 mb-3 p-2'>
            <div className='session-builder-card' aria-hidden='true' onClick={() => setShowRecurringSessionsModal(true)}>
              <ResetIcon aria-hidden='true' id='resetIcon' />
              <p className='session-card-title'>{t('programs.builder.schedule.addRecurringSessions')}</p>
              <p className='session-card-description'>
                {t('programs.builder.schedule.addRecurringSessionsDescription')}
              </p>
            </div>
          </div>

          <div className='col-12 col-lg-4 mb-3 p-2'>
            <div className='session-builder-card' aria-hidden='true' onClick={() => setShowCopyScheduleModal(true)}>
              <PackageIcon aria-hidden='true' id='packageIcon' />
              <p className='session-card-title'>{t('programs.builder.schedule.copyFromAnotherProgram')}</p>
              <p className='session-card-description'>
                {t('programs.builder.schedule.copyFromAnotherProgramDescription')}
              </p>
            </div>
          </div>
        </div>
      )}

      <SingleSessionModal
        showModal={showSingleSessionModal}
        closeModal={() => { setSelectedSession(null); setShowSingleSessionModal(false) }}
        selectedSession={selectedSession}
      />

      <RecurringSessionsModal
        showModal={showRecurringSessionsModal}
        closeModal={() => { setShowRecurringSessionsModal(false) }}
      />

      {showCopyScheduleModal && (
        <CopyScheduleModal
          showModal={showCopyScheduleModal}
          closeModal={() => { setShowCopyScheduleModal(false) }}
        />
      )}

      <ConfirmationModal2
        showModal={showConfirmationModal}
        closeModal={() => setShowConfirmationModal(false)}
        onDeny={() => setShowConfirmationModal(false)}
        denyMessage={t('common.cancel')}
        onProceed={handleDeleteAllSessions}
        proceedMessage={t('common.action.confirm')}
        icon={<WarningIcon aria-hidden='true' id='warningIcon' />}
        title={t('programs.builder.schedule.deleteAllSessionsTitle')}
        message={t('programs.builder.schedule.deleteAllSessionsMessage')}
      />
    </section>
  )
}

export default Schedule
